import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  Flame,
  TrendUp,
  FileText,
  Factory,
  Warning,
  ArrowUpRight,
} from "@phosphor-icons/react";
import { apiClient } from "../lib/api";

const FORCE_ICONS = {
  flame: Flame,
  trend_up: TrendUp,
  file_text: FileText,
  factory: Factory,
};

/**
 * May 2026 HECO Rate Briefing card.
 *
 * Props:
 *   utilityCode  - "HECO" | "MECO" | "HELCO" | "MOLOKAI" | "LANAI" | "KIUC"
 *   variant      - "dark" (landing page) | "light" (wizard results)
 */
export default function RateBriefingCard({ utilityCode, variant = "light" }) {
  const [briefing, setBriefing] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const params = utilityCode ? { utility_code: utilityCode } : {};
        const { data } = await apiClient.get("/rate-briefing", { params });
        if (!cancelled) setBriefing(data);
      } catch (e) {
        console.error("rate-briefing fetch failed", e);
      }
    })();
    return () => { cancelled = true; };
  }, [utilityCode]);

  if (!briefing) return null;

  const isDark = variant === "dark";
  const personalized = briefing.personalized;

  return (
    <div
      data-testid="rate-briefing-card"
      className={`rounded-3xl overflow-hidden border ${
        isDark
          ? "border-red-500/30 bg-gradient-to-br from-red-950/40 via-slate-950 to-amber-950/30"
          : "border-red-200 bg-gradient-to-br from-red-50 via-white to-amber-50"
      }`}
    >
      {/* Header strip */}
      <div
        className={`px-6 md:px-10 py-5 flex items-center justify-between flex-wrap gap-3 border-b ${
          isDark ? "border-white/10 bg-red-500/10" : "border-red-200/60 bg-red-100/50"
        }`}
      >
        <div className="flex items-center gap-3">
          <div
            className={`w-10 h-10 rounded-xl flex items-center justify-center ${
              isDark ? "bg-red-500/20 border border-red-500/40" : "bg-red-500 text-white"
            }`}
          >
            <Warning size={20} weight="fill" className={isDark ? "text-red-300" : "text-white"} />
          </div>
          <div>
            <div className={`text-[10px] font-bold uppercase tracking-[0.2em] ${
              isDark ? "text-red-300" : "text-red-700"
            }`}>
              Hawaii Residential Electric Rates · Briefing
            </div>
            <div className={`text-sm font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>
              {briefing.date}
            </div>
          </div>
        </div>
        {personalized && (
          <div
            data-testid="rate-briefing-personalized"
            className={`flex items-center gap-3 px-4 py-2 rounded-xl ${
              isDark ? "bg-amber-500/15 border border-amber-500/30" : "bg-amber-100 border border-amber-300"
            }`}
          >
            <div className="text-right">
              <div className={`text-[10px] font-bold uppercase tracking-wider ${
                isDark ? "text-amber-300" : "text-amber-800"
              }`}>
                Your island ({personalized.island})
              </div>
              <div className={`text-lg font-display font-bold ${isDark ? "text-white" : "text-slate-950"}`}>
                +{personalized.change_pct.toFixed(1)}% this month
              </div>
            </div>
            <ArrowUpRight size={28} weight="bold" className={isDark ? "text-amber-300" : "text-amber-600"} />
          </div>
        )}
      </div>

      {/* Headline */}
      <div className="px-6 md:px-10 pt-8 pb-6">
        <h3
          className={`font-display text-3xl md:text-4xl font-bold leading-tight tracking-tight ${
            isDark ? "text-white" : "text-slate-950"
          }`}
        >
          {briefing.headline}
        </h3>
        <p className={`mt-4 text-base leading-relaxed max-w-3xl ${isDark ? "text-slate-300" : "text-slate-600"}`}>
          {briefing.one_sentence}
        </p>
      </div>

      {/* Rate table */}
      <div className="px-6 md:px-10 pb-6">
        <div className={`text-[10px] font-bold uppercase tracking-[0.2em] mb-3 ${
          isDark ? "text-slate-400" : "text-slate-500"
        }`}>
          Schedule R top-tier rate — April → May 2026
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className={`text-left border-b ${isDark ? "border-white/10 text-slate-400" : "border-slate-200 text-slate-500"}`}>
                <th className="py-2 pr-4 text-[10px] font-bold uppercase tracking-wider">Island</th>
                <th className="py-2 pr-4 text-[10px] font-bold uppercase tracking-wider">Top tier begins</th>
                <th className="py-2 pr-4 text-[10px] font-bold uppercase tracking-wider">April</th>
                <th className="py-2 pr-4 text-[10px] font-bold uppercase tracking-wider">May</th>
                <th className="py-2 text-[10px] font-bold uppercase tracking-wider text-right">Change</th>
              </tr>
            </thead>
            <tbody>
              {briefing.islands.map((row) => {
                const isYou = personalized && personalized.utility_code === row.utility_code;
                return (
                  <tr
                    key={row.utility_code}
                    data-testid={`rate-row-${row.utility_code}`}
                    className={`border-b last:border-0 ${
                      isDark ? "border-white/5" : "border-slate-100"
                    } ${isYou ? (isDark ? "bg-amber-500/10" : "bg-amber-100/60") : ""}`}
                  >
                    <td className={`py-3 pr-4 font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>
                      {row.island}
                      {isYou && (
                        <span className={`ml-2 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${
                          isDark ? "bg-amber-500 text-slate-900" : "bg-amber-500 text-white"
                        }`}>You</span>
                      )}
                    </td>
                    <td className={`py-3 pr-4 font-mono text-xs ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                      &gt; {row.threshold_kwh.toLocaleString()} kWh
                    </td>
                    <td className={`py-3 pr-4 font-mono ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                      ${row.april_rate.toFixed(4)}
                    </td>
                    <td className={`py-3 pr-4 font-mono font-bold ${isDark ? "text-white" : "text-slate-950"}`}>
                      ${row.may_rate.toFixed(4)}
                    </td>
                    <td className="py-3 text-right">
                      <span className={`font-display font-bold text-base ${
                        row.change_pct >= 20 ? "text-red-500" : row.change_pct >= 10 ? "text-amber-500" : "text-amber-600"
                      }`}>
                        +{row.change_pct.toFixed(1)}%
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Four forces */}
      <div className={`px-6 md:px-10 pb-8 ${isDark ? "" : "border-t border-slate-200/60"}`}>
        <div className={`text-[10px] font-bold uppercase tracking-[0.2em] mb-4 mt-4 ${
          isDark ? "text-amber-300" : "text-amber-700"
        }`}>
          Four forces stacking on top of what you just saw
        </div>
        <div className="grid md:grid-cols-2 gap-3">
          {briefing.forces.map((force, i) => {
            const Icon = FORCE_ICONS[force.icon] || Warning;
            return (
              <motion.div
                key={force.id}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.35, delay: i * 0.06 }}
                data-testid={`rate-force-${force.id}`}
                className={`flex gap-3 p-4 rounded-2xl border ${
                  isDark
                    ? "bg-white/5 border-white/10 hover:bg-white/10"
                    : "bg-white border-slate-200 hover:border-amber-300 hover:shadow-sm"
                } transition-all`}
              >
                <div
                  className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                    isDark ? "bg-amber-500/20 border border-amber-500/40" : "bg-amber-100"
                  }`}
                >
                  <Icon size={20} weight="duotone" className={isDark ? "text-amber-300" : "text-amber-600"} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-[10px] font-bold font-mono ${isDark ? "text-slate-500" : "text-slate-400"}`}>
                      0{i + 1}
                    </span>
                    <h4 className={`font-semibold text-sm leading-snug ${isDark ? "text-white" : "text-slate-900"}`}>
                      {force.title}
                    </h4>
                  </div>
                  <p className={`text-xs mt-1.5 leading-relaxed ${isDark ? "text-slate-400" : "text-slate-600"}`}>
                    {force.detail}
                  </p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* What this means */}
      <div
        className={`px-6 md:px-10 py-6 ${
          isDark
            ? "bg-gradient-to-r from-emerald-500/10 via-sky-500/5 to-transparent border-t border-emerald-500/20"
            : "bg-gradient-to-r from-emerald-50 via-sky-50 to-white border-t border-emerald-200/60"
        }`}
      >
        <div className={`text-[10px] font-bold uppercase tracking-[0.2em] mb-2 ${
          isDark ? "text-emerald-300" : "text-emerald-700"
        }`}>
          What this means for you
        </div>
        <p className={`text-sm leading-relaxed ${isDark ? "text-slate-200" : "text-slate-700"}`}>
          <span className={`font-semibold ${isDark ? "text-white" : "text-slate-950"}`}>
            A rooftop solar system locks in your rate for 25+ years. The grid does not.
          </span>{" "}
          {briefing.what_this_means}
        </p>
        <p className={`text-[10px] mt-4 italic ${isDark ? "text-slate-500" : "text-slate-400"}`}>
          Source: {briefing.source}
        </p>
      </div>
    </div>
  );
}
